import React from 'react';
import DataCard from '../common/DataCard';
import Loader from '../common/Loader';
import Pagination from '../common/Pagination';
import ImagePreview from '../common/ImagePreview';
import { MemberCompetencyDTO, MemberFirearmDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { renderBooleanBadge } from '../../utils/displayUtils';
import IconButton from '../common/IconButton';
import { Pen, Phone, RefreshCcw, Trash } from 'lucide-react';

import { getCompetencyExpiryInfo, getCompetencyStatus, competencyStatusLabel } from '../../utils/competencyUtils';
import { useNavigate } from 'react-router-dom';

interface MemberCompetencyDataCardProps {
    data: MemberCompetencyDTO[];
    handleEdit: (row: MemberCompetencyDTO) => void;
    handleRewal: () => void;
    handleDelete: (row: MemberCompetencyDTO) => void;
    currentPage: number;
    totalPages: number;
    setCurrentPage: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    isLoading: boolean;

    // Optional action renderers injected by the List page
    renderHeaderActions?: (row: MemberCompetencyDTO) => React.ReactNode;
    renderPrimaryActions?: (row: MemberCompetencyDTO) => React.ReactNode;
    renderSecondaryActions?: (row: MemberCompetencyDTO) => React.ReactNode;
}

const MemberCompetencyDataCard: React.FC<MemberCompetencyDataCardProps> = ({
    data,
    handleEdit,
    handleRewal,
    handleDelete,
    currentPage,
    totalPages,
    setCurrentPage,
    pageSize,
    setPageSize,
    isLoading,
    renderHeaderActions,
    renderPrimaryActions,
    renderSecondaryActions,
}) => {

    const navigate = useNavigate();

    return (
        <div>
            {isLoading ? (
                <div className="py-8 flex justify-center"><Loader /></div>
            ) : data.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">No records found</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {data.map((row) => {
                        const title = row.competencyType?.title || 'Unknown';

                        const status = getCompetencyStatus(row);
                        const isGood = status === 'active';
                        const falseVariant = status === 'expired' ? 'error' : 'warning';
                        const leftBadge = renderBooleanBadge(isGood, competencyStatusLabel[status], competencyStatusLabel[status], falseVariant);

                        const rightMeta = status === 'expired'
                            ? { label: 'Expired', date: row.competencyExpiryDate }
                            : { label: 'Expires', date: row.competencyExpiryDate };

                        return (
                            <DataCard<MemberCompetencyDTO>
                                key={row.id}
                                item={row}
                                collapsibleOnMobile={true}
                                leftBadgeNode={() => leftBadge}
                                rightMeta={() => {
                                    const info = getCompetencyExpiryInfo(row.competencyExpiryDate);
                                    return {
                                        label: rightMeta.label,
                                        value: <span className={info.className}>
                                            {rightMeta.date ? convertToUserTime(String(rightMeta.date), true) : 'n/a'}
                                        </span>,
                                    };
                                }}
                                title={() => title}
                                headerActions={renderHeaderActions}
                                fields={[
                                    { label: 'Certificate Number', render: (row) => row.certificateNumber ?? 'Unknown' },
                                    {
                                        label: 'Date issued',
                                        render: (row: MemberCompetencyDTO) =>
                                            convertToUserTime(String(row.competencyIssueDate ?? 'n/a'), true),
                                    },
                                    {
                                        label: 'Renew By',
                                        render: (row) => {
                                            const info = getCompetencyExpiryInfo(row.competencyExpiryDate);
                                            return <span className={info.className}>{info.display}</span>;
                                        }
                                    }

                                ]}
                                files={[{
                                    label: 'Certificate',
                                    id: row.competencyCertificateId ?? ''
                                }]}
                                primaryActions={
                                    renderPrimaryActions ??
                                    ((row) => (
                                        <div className="flex justify-between w-full">
                                            <div>
                                                {getCompetencyStatus(row) === 'expired' && (
                                                    <IconButton
                                                        label='Contact Admin'
                                                        color="blue" icon={<Phone />}
                                                        onClick={() => navigate('/contact')}
                                                    />
                                                )}
                                                {(getCompetencyStatus(row) === 'active' || getCompetencyStatus(row) === 'expiring') && (
                                                    <IconButton
                                                        label='Renew'
                                                        color="green" icon={<RefreshCcw />}
                                                        onClick={() => handleRewal()} />
                                                )}
                                            </div>
                                            <div className="flex gap-3">
                                                <IconButton
                                                    label='Edit'
                                                    color="orange"
                                                    icon={<Pen />}
                                                    onClick={() => handleEdit(row)} />
                                                <IconButton
                                                    label='Delete'
                                                    color="red"
                                                    icon={<Trash />}
                                                    onClick={() => handleDelete(row)} />
                                            </div>
                                        </div>
                                    ))
                                }
                                secondaryActions={renderSecondaryActions}
                            />
                        );
                    })}
                </div>
            )}

            {!isLoading && data.length > 0 && (
                <div className="mt-4">
                    <Pagination
                        currentPage={Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1}
                        totalPages={Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1}
                        onPageChange={setCurrentPage}
                        pageSize={Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 12}
                        onPageSizeChange={setPageSize}
                    />
                </div>
            )}
        </div>
    );
};

export default MemberCompetencyDataCard;
